import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createBooking } from '../../api/booking';
import { getServices } from '../../api/service';
import Swal from 'sweetalert2';
import '../../styles/createBooking.css';

function CreateBooking() {
  const { serviceId: routeServiceId } = useParams();
  const [serviceId, setServiceId] = useState(routeServiceId || '');
  const [services, setServices] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [sessionCount, setSessionCount] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();

  // Fetch all available services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data } = await getServices();
        setServices(data);
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error fetching services',
          text: error.response?.data?.message || error.message,
        });
      }
    };
    fetchServices();
  }, []);

  // Handle booking submission
  const handleBooking = async () => {
    if (!serviceId || !scheduledAt) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please select a service and time.',
      });
      return;
    }

    if (sessionCount < 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Session Count',
        text: 'Session count must be at least 1.',
      });
      return;
    }

    if (!agreedToTerms) {
      Swal.fire({
        icon: 'warning',
        title: 'Agreement Required',
        text: 'You must agree to the acknowledgment terms before booking.',
      });
      return;
    }

    try {
      const bookingPayload = {
        service: serviceId,
        scheduledAt,
        sessionCount,
        acknowledgement: true,
      };

      const { data } = await createBooking(bookingPayload);

      Swal.fire({
        icon: 'success',
        title: 'Booking Created!',
        text: 'Redirecting to payment...',
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        navigate(`/payment/${data._id}`, { state: { amount: data.amount } });
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        text: error.response?.data?.message || error.message,
      });
    }
  };

  return (
    <div className="booking-container">
      <h2>Create a Booking</h2>

      <div className="booking-form">
        {/* Service selection */}
        <select
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
          disabled={!!routeServiceId} // Disable if coming from a specific service link
        >
          <option value="">Select a Service</option>
          {services.map((service) => (
            <option key={service._id} value={service._id}>
              {service.name}
            </option>
          ))}
        </select>

        {/* Schedule datetime */}
        <input
          type="datetime-local"
          name="scheduledAt"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          required
        />

        {/* Session count */}
        <div className="session-count">
          <label htmlFor="sessionCount">Number of Sessions:</label>
          <input
            type="number"
            id="sessionCount"
            min="1"
            value={sessionCount}
            onChange={(e) => setSessionCount(Number(e.target.value))}
          />
        </div>

        {/* Terms and acknowledgment */}
        <div className="terms-checkbox">
          <label>
            <span className="acknowledgment-label">
              I have read and agree to the •
              <span className="tooltip">
                Acknowledgment 
                <div className="tooltiptext scrollable-terms">
                  <h4>Terms & Conditions</h4>
                  <p><strong>Terms and Conditions for Onenesssence / Spiritual and Oneness Coach Divya Jyoti</strong></p>
                  <p><strong>1. Scope of Services</strong><br />
                    By using services offered by Onenesssence / Spiritual and Oneness Coach Divya Jyoti—including coaching, healing sessions, past life regressions, meditations, advice, and workshops—you agree to these terms. These apply to services provided in person or online, and to communication via text, phone, email, or social media.
                  </p>
                  <p><strong>2. Nature of Services</strong><br />
                    Services are based on energy work, yoga philosophy, spiritual healing, and coaching. They are meant for personal growth and support.
                  </p>
                  <p><strong>3. Not a Substitute for Medical Care</strong><br />
                    Onenesssence / Spiritual and Oneness Coach Divya Jyoti and staff are not licensed medical or mental health professionals. Services do not replace medical, psychological, or psychiatric care. Clients should seek advice from qualified healthcare providers when needed.
                  </p>
                  <p><strong>4. Personal Responsibility</strong><br />
                    By participating, you accept full responsibility for your physical, mental, emotional, and financial well-being. You understand that results depend on your personal effort and openness.
                  </p>
                  <p><strong>5. Right to Discontinue Services</strong><br />
                    Onenesssence / Spiritual and Oneness Coach Divya Jyoti may refuse or stop services at any time. Clients engaging in abusive, hateful, or inappropriate behaviour will have services terminated with no refund.
                  </p>
                  <p><strong>6. Refund & Cancellation Policy</strong><br />
                    – All payments are final and non-refundable.<br />
                    – Individual session rescheduling requires at least 24-hour notice; sessions must be used within 15 days. Late arrivals over 10 minutes count as no-shows and are non-refundable.<br />
                    – If Onenesssence cancels a service, you will be offered a reschedule or, if that isn’t possible, a refund within one month.<br />
                    – Registrations for workshops, events, or group coaching are non-refundable and cannot be rescheduled if cancelled by you.
                  </p>
                  <p><strong>7. Privacy & Confidentiality</strong><br />
                    Session recordings are prohibited unless approved. Sharing session content or private communication outside the session is not allowed. Violations may lead to termination of services without refund.
                  </p>
                  <p><strong>8. Payment Terms</strong><br />
                    Payments can be made via cash, Google Pay, bank transfer, credit card, PayPal, or Stripe. Fees are for the time, expertise, and energy exchange, and do not guarantee specific outcomes.
                  </p>
                  <p><strong>9. Unused Services</strong><br />
                    Credits, packages, or prepaid services must be used within the timeframe stated at booking or purchase.
                  </p>
                  <p><strong>10. Disputes & Liability Release</strong><br />
                    If a dispute arises, Onenesssence / Spiritual and Oneness Coach Divya Jyoti may end services immediately. By participating, you release Onenesssence / Spiritual and Oneness Coach Divya Jyoti and staff from claims for damages of any kind.
                  </p>
                  <p><strong>11. No Legal or Social Media Action</strong><br />
                    You agree not to pursue legal claims or negative social media action against Onenesssence, its staff, or Spiritual and Oneness Coach Divya Jyoti.
                  </p>
                  <p><em>By participating, you confirm that you understand and accept these terms, and that you take full responsibility for your own well-being and choices.</em></p>
                </div>
              </span>
            </span>
                <span className="checkbox-wrapper">
      <input
        type="checkbox"
        checked={agreedToTerms}
        onChange={() => setAgreedToTerms(!agreedToTerms)}
      />
    </span>
          </label>
        </div>

        <button onClick={handleBooking}>Create Booking</button>
      </div>
    </div>
  );
}

export default CreateBooking;
